import { useStrings } from "@1js/localization-components";
import type { Group, Prompt } from "@1js/pl-types";
import {
  Button,
  Combobox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Label,
  Tooltip,
  makeStyles,
  useId,
  type ComboboxOpenChangeData,
  type ComboboxOpenEvents,
  type OptionOnSelectData,
  type SelectionEvents,
} from "@fluentui/react-components";
import { Dismiss20Regular } from "@fluentui/react-icons";
import * as React from "react";
import { GroupOption } from "../GroupOption/GroupOption";
import { PromptCard } from "../PromptCard/PromptCard";
import type { Appearance } from "../data";
import {
  CloseTooltip,
  ShareToTeamCancelButtonLabel,
  ShareToTeamShareButtonLabel,
  ShareToTeamTeamsDropdownPlaceholder,
  ShareToTeamTeamsLabel,
  ShareToTeamTitle,
} from "./ShareToWorkgroupDialog.strings";

export type ShareToWorkgroupDialogProps = {
  groups?: Group[];
  prompts?: Prompt[];
  isOpen: boolean;
  onClose: (isClosedByCustomer: boolean, prompts: Prompt[]) => void;
  onShare: (prompts: Prompt[], groupIds: string[]) => void;
  appearance: Appearance;
  isAllowMultiSelect: boolean;
  onGroupDropdownOpenChange?: (prompt: Prompt, isOpen: boolean) => void;
  onGroupDropdownOptionSelect?: (prompt: Prompt) => void;
};

const useStyles = makeStyles({
  promptCardContainer: {
    backgroundImage: [
      "linear-gradient(90deg, rgba(71, 207, 250, 0.10) 0%, rgba(180, 124, 248, 0.10) 100%)",
    ],
    paddingTop: "20px",
    paddingBottom: "20px",
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    width: "100%",
    marginBottom: "14px",
  },
  promptCardContainerInner: {
    maxWidth: "260px",
  },
  combobox: {
    width: "100%",
    marginBottom: "36px",
  },
});

export const ShareToWorkgroupDialog: React.FC<ShareToWorkgroupDialogProps> = ({
  groups,
  prompts,
  isOpen,
  onClose,
  onShare,
  appearance,
  isAllowMultiSelect,
  onGroupDropdownOpenChange,
  onGroupDropdownOptionSelect,
}) => {
  const [groupIds, setGroupIds] = React.useState<string[]>([]);
  const [isButtonEnabled, setIsButtonEnabled] = React.useState(false);
  const styles = useStyles();

  const handleClose = React.useCallback(() => {
    onClose(true, prompts as Prompt[]);
  }, [onClose, prompts]);

  const handleShare = React.useCallback(() => {
    onShare(prompts as Prompt[], groupIds);
    onClose(false, prompts as Prompt[]);
  }, [groupIds, onClose, onShare, prompts]);

  const localizations = useStrings({
    ShareToTeamCancelButtonLabel,
    ShareToTeamShareButtonLabel,
    ShareToTeamTitle,
    ShareToTeamTeamsLabel,
    ShareToTeamTeamsDropdownPlaceholder,
    CloseTooltip,
  });

  React.useEffect(() => {
    if (!isOpen) {
      // reset the dialog state when it's closed
      setGroupIds([]);
      setIsButtonEnabled(false);
    }
  }, [isOpen]);

  const onSelectedGroupChange = React.useCallback(
    (_event: SelectionEvents, data: OptionOnSelectData) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      setGroupIds(data.selectedOptions);
      setIsButtonEnabled(data.selectedOptions.length > 0);
      const prompt = prompts?.[0];
      if (prompt && onGroupDropdownOptionSelect) {
        onGroupDropdownOptionSelect(prompt);
      }
    },
    [onGroupDropdownOptionSelect, prompts]
  );

  const handleOnGroupDropdownOpenChange = React.useCallback(
    (_: ComboboxOpenEvents, data: ComboboxOpenChangeData) => {
      const prompt = prompts?.[0];
      if (prompt && onGroupDropdownOpenChange) {
        onGroupDropdownOpenChange(prompt, data.open);
      }
    },
    [prompts, onGroupDropdownOpenChange]
  );

  const groupDropdownId = useId("group-dropdown");

  if (!prompts || prompts.length === 0 || !groups || groups.length === 0) {
    return <></>;
  }

  const promptToShare = (prompts as Prompt[])[0];

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(_event, data) => {
        if (!data.open) {
          handleClose();
        }
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle
            action={
              <Tooltip
                content={localizations.CloseTooltip()}
                relationship="label"
              >
                <DialogTrigger action="close">
                  <Button
                    data-testid="close-dialog"
                    appearance="subtle"
                    aria-label="close"
                    icon={<Dismiss20Regular />}
                  />
                </DialogTrigger>
              </Tooltip>
            }
          >
            {localizations.ShareToTeamTitle()}
          </DialogTitle>
          <DialogContent>
            <div className={styles.promptCardContainer}>
              <div className={styles.promptCardContainerInner}>
                <PromptCard
                  prompt={promptToShare}
                  appearance={appearance}
                  isPromptSharingEnabled={false}
                  isPromptSharingViaEmailEnabled={false}
                  isPromptSharingToWorkgroupEnabled={false}
                  isPromptRemovingFromWorkgroupEnabled={false}
                  isPromptRingTagEnabled={false}
                  isBadgesEnabled={false}
                  isFavoriteEnabled={false}
                  isShowPromptAuthor={true}
                />
              </div>
            </div>
            <Label id={groupDropdownId} required={true} size="large">
              {localizations.ShareToTeamTeamsLabel()}
            </Label>
            <br />
            <Combobox
              placeholder={localizations.ShareToTeamTeamsDropdownPlaceholder()}
              selectedOptions={groupIds}
              onOptionSelect={onSelectedGroupChange}
              aria-labelledby={groupDropdownId}
              size="large"
              className={styles.combobox}
              data-testid="group-combobox"
              multiselect={isAllowMultiSelect}
              onOpenChange={handleOnGroupDropdownOpenChange}
            >
              {groups
                ?.filter((group: Group) => group.IsOwner)
                .map((group: Group) => (
                  <GroupOption group={group} key={group.Id} />
                ))}
            </Combobox>
          </DialogContent>
          <DialogActions>
            <Button
              appearance="primary"
              onClick={handleShare}
              disabled={!isButtonEnabled}
            >
              {localizations.ShareToTeamShareButtonLabel()}
            </Button>
            <Button appearance="secondary" onClick={handleClose}>
              {localizations.ShareToTeamCancelButtonLabel()}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
